import type { ColumnsType, ColumnType } from 'antd/es/table'
import type { CSSProperties } from 'react'

export type TreadmillLeaf<T> = ColumnType<T> & { width: number }

export const JUSTIFY_BY_ALIGN: Record<string, CSSProperties['justifyContent']> =
  {
    center: 'center',
    right: 'flex-end'
  }

export type ColumnLayout<T> = {
  head: Array<TreadmillLeaf<T>>
  headLefts: number[]
  body: Array<TreadmillLeaf<T>>
  bodyOffset: number
  bodyLefts: number[]
  tail: Array<TreadmillLeaf<T>>
  tailOffsets: number[]
}

export type ColumnStateMap = Record<
  string,
  { show?: boolean; order?: number; fixed?: 'left' | 'right' | null }
>

export const isSameColumnState = (
  previous: ColumnStateMap | undefined,
  next: ColumnStateMap | undefined
): boolean => {
  if (previous === next) {
    return true
  }

  if (!previous || !next) {
    return false
  }

  const previousKeys = Object.keys(previous)

  if (previousKeys.length !== Object.keys(next).length) {
    return false
  }

  return previousKeys.every(
    (key) =>
      previous[key]?.show === next[key]?.show &&
      previous[key]?.order === next[key]?.order &&
      previous[key]?.fixed === next[key]?.fixed
  )
}

export const columnStateKey = <T>(
  column: ColumnsType<T>[number],
  index: number
): string => {
  const source =
    ('key' in column && column.key) ||
    ('dataIndex' in column ? column.dataIndex : undefined)

  if (source === undefined || source === null || source === '') {
    return String(index)
  }

  return Array.isArray(source) ? source.join('-') : String(source)
}

export type ColumnPin = 'left' | 'right'

export type ColumnPins = Record<string, ColumnPin | undefined>

export const definedPinOf = <T>(
  column: ColumnsType<T>[number]
): ColumnPin | undefined =>
  column.fixed === 'right'
    ? 'right'
    : column.fixed === 'left' || column.fixed === true
      ? 'left'
      : undefined

const EDGE_RANK: Record<ColumnPin, number> = { left: 0, right: 2 }
const UNPINNED_RANK = 1

const edgeRankOf = <T>(column: ColumnsType<T>[number]): number => {
  const pin = definedPinOf(column)

  return pin ? EDGE_RANK[pin] : UNPINNED_RANK
}

export const keepPinsAtEdges = <T>(columns: ColumnsType<T>): ColumnsType<T> =>
  [...columns].sort((a, b) => edgeRankOf(a) - edgeRankOf(b))

export const columnPinsOf = <T>(columns: ColumnsType<T>): ColumnPins =>
  Object.fromEntries(
    columns.map((column, index) => [
      columnStateKey(column, index),
      definedPinOf(column)
    ])
  )

export const toProColumnsState = (
  columnsMap: ColumnStateMap | undefined,
  definedPins: ColumnPins
) =>
  columnsMap &&
  Object.fromEntries(
    Object.entries(columnsMap).map(([key, state]) => [
      key,
      {
        ...state,
        fixed: 'fixed' in state ? (state.fixed ?? undefined) : definedPins[key]
      }
    ])
  )

type ProColumnsStateSource = {
  onChange?: unknown
  persistenceKey?: unknown
  persistenceType?: unknown
}

export const proTableColumnsState = <
  Source extends ProColumnsStateSource,
  Value
>(
  source: Source,
  value: Value
) => {
  const {
    onChange: _changedByContainer,
    persistenceKey: _persistedByContainer,
    persistenceType: _persistedInLocalStorage,
    ...proColumnsState
  } = source

  return { ...proColumnsState, value }
}

export const applyColumnState = <T>(
  columns: ColumnsType<T>,
  columnsMap: ColumnStateMap | undefined,
  isNested = false
): ColumnsType<T> => {
  if (!columnsMap) {
    return columns
  }

  const kept = columns
    .map((column, index) => ({ column, index }))
    .filter(({ column, index }) => {
      const state = columnsMap[columnStateKey(column, index)]

      return state?.show !== false
    })
    .map(({ column, index }) => {
      const children =
        'children' in column && Array.isArray(column.children)
          ? applyColumnState(column.children, columnsMap, true)
          : undefined

      const state = columnsMap[columnStateKey(column, index)]
      const pinned = state && 'fixed' in state
      const patched =
        children || pinned
          ? {
              ...column,
              ...(children ? { children } : {}),
              ...(pinned ? { fixed: state.fixed ?? undefined } : {})
            }
          : column

      return { column: patched, index, order: state?.order }
    })

  if (isNested) {
    return kept.map(({ column }) => column) as ColumnsType<T>
  }

  const hasExplicitOrder = kept.some((entry) => entry.order !== undefined)

  if (hasExplicitOrder) {
    kept.sort((a, b) => (a.order ?? a.index) - (b.order ?? b.index))
  }

  return keepPinsAtEdges(kept.map(({ column }) => column)) as ColumnsType<T>
}

export const readCellValue = <T>(record: T, dataIndex: unknown): unknown => {
  const path =
    dataIndex === null || dataIndex === undefined || dataIndex === ''
      ? []
      : Array.isArray(dataIndex)
        ? dataIndex
        : [dataIndex]

  return path.reduce<unknown>(
    (value, segment) =>
      value === null || value === undefined
        ? value
        : (value as Record<PropertyKey, unknown>)[segment as PropertyKey],
    record
  )
}

export const COLUMN_MIN_WIDTH = 104

export const HEADER_TITLE_CLEARANCE = 10

export const COLUMN_MAX_WIDTH_SHARE = 0.6

export const COLUMN_MAX_WIDTH_FLOOR = 320

export const columnWidthVar = (key: unknown): string =>
  `--twc-${String(key).replace(/[^A-Za-z0-9_-]/g, '_')}`

export const headerMinWidth = (
  contentWidth: number,
  horizontalPadding: number
): number =>
  Math.max(
    COLUMN_MIN_WIDTH,
    Math.ceil(contentWidth + horizontalPadding + HEADER_TITLE_CLEARANCE)
  )

export const columnMaxWidth = (
  containerWidth: number,
  minWidth: number
): number =>
  Math.max(
    minWidth,
    COLUMN_MAX_WIDTH_FLOOR,
    Math.round(containerWidth * COLUMN_MAX_WIDTH_SHARE)
  )

export const resizedColumnWidth = (
  startWidth: number,
  startX: number,
  clientX: number,
  minWidth: number = COLUMN_MIN_WIDTH,
  maxWidth: number = Number.POSITIVE_INFINITY
): number =>
  Math.min(maxWidth, Math.max(minWidth, startWidth + clientX - startX))

export const slotRealIndex = (
  startIndex: number,
  slot: number,
  poolSize: number
): number =>
  startIndex + ((((slot - startIndex) % poolSize) + poolSize) % poolSize)

export const isSkimJump = (
  previousStart: number,
  nextStart: number,
  poolSize: number
): boolean => Math.abs(nextStart - previousStart) >= Math.max(1, poolSize / 2)

type HeaderPin = { left?: number; right?: number }

export type HeaderPinOffsets = Map<string, HeaderPin>

export const headerPinOffsetsOf = <T>({
  head,
  headLefts,
  tail,
  tailOffsets
}: ColumnLayout<T>): HeaderPinOffsets =>
  new Map<string, HeaderPin>([
    ...head.map((leaf, index): [string, HeaderPin] => [
      String(leaf.key),
      { left: headLefts[index] }
    ]),
    ...tail.map((leaf, index): [string, HeaderPin] => [
      String(leaf.key),
      { right: tailOffsets[index] }
    ])
  ])

export const buildColumnLayout = <T>(
  leaves: Array<TreadmillLeaf<T>>,
  selectionWidth: number
): ColumnLayout<T> => {
  const head: Array<TreadmillLeaf<T>> = []
  const body: Array<TreadmillLeaf<T>> = []
  const tail: Array<TreadmillLeaf<T>> = []

  leaves.forEach((leaf) => {
    if (leaf.fixed === 'left' || leaf.fixed === true) {
      head.push(leaf)
    } else if (leaf.fixed === 'right') {
      tail.push(leaf)
    } else {
      body.push(leaf)
    }
  })

  const headLefts: number[] = []
  let headAccumulated = selectionWidth

  head.forEach((leaf) => {
    headLefts.push(headAccumulated)
    headAccumulated += leaf.width
  })

  const bodyLefts: number[] = []
  let bodyAccumulated = 0

  body.forEach((leaf) => {
    bodyLefts.push(bodyAccumulated)
    bodyAccumulated += leaf.width
  })

  const tailOffsets: number[] = []
  let tailAccumulated = 0

  for (let index = tail.length - 1; index >= 0; index -= 1) {
    tailOffsets[index] = tailAccumulated
    tailAccumulated += tail[index].width
  }

  return {
    head,
    headLefts,
    body,
    bodyOffset: headAccumulated,
    bodyLefts,
    tail,
    tailOffsets
  }
}

export const HORIZONTAL_WINDOW_MIN_COLUMNS = 16

export const HORIZONTAL_OVERSCAN_PX = 320

export type ColumnWindow = {
  first: number
  last: number
  before: number
  after: number
}

export const bodyColumnWindow = (
  lefts: number[],
  widths: number[],
  visibleStart: number,
  visibleEnd: number
): ColumnWindow => {
  const count = widths.length
  const total = count ? lefts[count - 1] + widths[count - 1] : 0
  const firstVisible = lefts.findIndex(
    (left, index) => left + widths[index] > visibleStart
  )
  const first = firstVisible === -1 ? Math.max(0, count - 1) : firstVisible
  const lastVisible = lefts.filter((left) => left < visibleEnd).length - 1
  const last = Math.max(first, lastVisible)

  return {
    first,
    last,
    before: lefts[first] ?? 0,
    after: count ? total - (lefts[last] + widths[last]) : 0
  }
}

export const isSameColumnWindow = (
  previous: ColumnWindow | undefined,
  next: ColumnWindow | undefined
) =>
  previous?.first === next?.first &&
  previous?.last === next?.last &&
  previous?.before === next?.before &&
  previous?.after === next?.after
